import { FC } from "react";
import { Stack, Typography } from "@mui/material";
import { TotalCostColumnHeaderCell } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/header-cell-components/TotalCostColumnHeaderCell";
import { PrevPeriodCostColumnHeaderCell } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/header-cell-components/PrevPeriodCostColumnHeaderCell";
import { TrendColumnHeaderCell } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/header-cell-components/TrendColumnHeaderCell";
import { ForecastedCostHeaderCell } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/header-cell-components/ForecastedCostHeaderCell";
import { generateBillingExplorerTotalCostTrend } from "@/store/resource-explorer/utils/helpers/generateBillingExplorerTotalCostTrend";
import {
  ResourceExplorerGroupingNullable,
  ForecastedCost,
} from "@/services/cloudchipr.api";
import { ResourceExplorerGroupingLabelMap } from "@/components/pages/common/resource-explorer-grouping-selector-v2/utils/types/types";
import { LabelColumnHeaderCell } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/header-cell-components/LabelColumnHeaderCell";

interface InternalBillingExplorerTotalHeaderRendererProps {
  col: any;
  index: number;
  groupBy: ResourceExplorerGroupingNullable;
  groupingLabels?: ResourceExplorerGroupingLabelMap;
  totalCost: number;
  previousPeriodTotalCost: number | null;
  forecasted_costs: ForecastedCost[];
  dataCount: number;
}

export const InternalBillingExplorerTotalHeaderRenderer: FC<
  InternalBillingExplorerTotalHeaderRendererProps
> = ({
  col,
  index,
  groupBy,
  groupingLabels,
  totalCost,
  previousPeriodTotalCost,
  forecasted_costs,
  dataCount,
}) => {
  if (index === 0) {
    const label = groupBy
      ? (groupingLabels?.[groupBy]?.singular ?? groupBy ?? "")
      : "Total";

    return (
      <LabelColumnHeaderCell
        grouping={groupBy}
        dataCount={dataCount}
        groupingLabel={label}
      />
    );
  }

  switch (col.field) {
    case "total_cost":
      return <TotalCostColumnHeaderCell totalCost={totalCost} />;

    case "previous_period_total_cost":
      return <PrevPeriodCostColumnHeaderCell sum={previousPeriodTotalCost} />;

    case "trend": {
      const trend = generateBillingExplorerTotalCostTrend(
        totalCost,
        previousPeriodTotalCost ?? 0,
      );
      return <TrendColumnHeaderCell trend={trend} />;
    }

    case "monthly_forecast": {
      const monthlyCost = forecasted_costs?.find(
        (item) => item.forecast_option.toLowerCase() === "month",
      );
      return (
        <ForecastedCostHeaderCell
          forecastedCost={monthlyCost}
          forecastOption="month"
        />
      );
    }

    case "quarterly_forecast": {
      const quarterlyCost = forecasted_costs?.find(
        (item) => item.forecast_option.toLowerCase() === "quarter",
      );
      return (
        <Stack width={175}>
          <ForecastedCostHeaderCell
            forecastedCost={quarterlyCost}
            forecastOption="quarter"
          />
        </Stack>
      );
    }
  }

  return (
    <Typography
      fontWeight="medium"
      variant="body2"
      color="text.primary"
      align="left"
    >
      {col.headerName || col.field || ""}
    </Typography>
  );
};
